import { useCallback } from "react"

import type { BannerData, HomeService } from "@be-miniapp/api"
import { APP_VERSION } from "@be-miniapp/app-configs"
import { useSelectedServiceStore } from "@be-miniapp/shared/stores/current-service"
import { removePackageNameInRoute } from "@be-miniapp/shared/utils/string"
import { useNavigate } from "@be-miniapp/ui/hooks/use-navigate"

import { fromSemverToVersion } from "@/utils/version"

/**
 * Custom hook for home page actions
 * Separates business logic from UI components
 */
export function useHomeActions(
  lang: string,
  fetchLocationBeforeRide: () => Promise<unknown>
) {
  const navigate = useNavigate()
  const setSelectedService = useSelectedServiceStore(
    (state) => state.setService
  )

  const handleServiceClick = useCallback(
    async (service: HomeService) => {
      setSelectedService(service.title[lang], service.image)

      if (!service.is_enabled) {
        return
      }

      if (service.web_version > fromSemverToVersion(APP_VERSION)) {
        navigate("/non-supported")
        return
      }

      const path = removePackageNameInRoute(service.url)

      if (path.startsWith("/ride/dropoff_location")) {
        await fetchLocationBeforeRide()
      }

      navigate(path, {
        state: {
          resetLocation: true,
        },
      })
    },
    [setSelectedService, lang, navigate, fetchLocationBeforeRide]
  )

  const handleSlideClick = useCallback(
    (slide: BannerData) => {
      const miniappLink = "https://zalo.me/s/2517980822879468590"

      if (slide.url.startsWith(miniappLink)) {
        navigate(slide.url.slice(miniappLink.length))
        return
      }

      navigate(removePackageNameInRoute(slide.url))
    },
    [navigate]
  )

  return {
    handleServiceClick,
    handleSlideClick,
  }
}
